import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas';
import {
    ICustomSeriesPaneRenderer,
    PaneRendererCustomData,
    PriceToCoordinateConverter,
    Time,
} from 'lightweight-charts';
import { VolumeCandleData } from './data';
import { VolumeCandleSeriesOptions } from './options';



export class VolumeCandleRenderer implements ICustomSeriesPaneRenderer {
    _data: PaneRendererCustomData<Time, VolumeCandleData> | null = null;
    _options: VolumeCandleSeriesOptions | null = null;

    draw(
        target: CanvasRenderingTarget2D,
        priceConverter: PriceToCoordinateConverter
    ): void {
        target.useBitmapCoordinateSpace((scope) =>
            this._drawImpl(scope, priceConverter)
        );
    }

    update(
        data: PaneRendererCustomData<Time, VolumeCandleData>,
        options: VolumeCandleSeriesOptions
    ): void {
        this._data = data;
        this._options = options;
    }

    _drawImpl(
        renderingScope: BitmapCoordinatesRenderingScope,
        priceToCoordinate: PriceToCoordinateConverter
    ): void {
        if (
            this._data === null ||
            this._data.bars.length === 0 ||
            this._data.visibleRange === null ||
            this._options === null
        ) {
            return;
        }

        const { bars, visibleRange, barSpacing } = this._data;
        const options = this._options;

        // Filter visible bars
        const visibleBars = [];
        for (let i = visibleRange.from; i < visibleRange.to; i++) {
            visibleBars.push(bars[i]);
        }

        if (visibleBars.length === 0) return;

        // Calculate max volume in visible range for scaling
        let maxVolume = 0;
        for (const bar of visibleBars) {
            if (bar.originalData.volume > maxVolume) {
                maxVolume = bar.originalData.volume;
            }
        }

        const ctx = renderingScope.context;
        const horizontalPixelRatio = renderingScope.horizontalPixelRatio;
        const verticalPixelRatio = renderingScope.verticalPixelRatio;

        ctx.lineCap = 'butt';
        ctx.lineJoin = 'round';

        const wickWidth = Math.max(1.5, Math.floor(horizontalPixelRatio));

        for (const bar of visibleBars) {
            const x = bar.x * horizontalPixelRatio;
            const open = priceToCoordinate(bar.originalData.open);
            const high = priceToCoordinate(bar.originalData.high);
            const low = priceToCoordinate(bar.originalData.low);
            const close = priceToCoordinate(bar.originalData.close);

            if (open === null || high === null || low === null || close === null) continue;

            const yOpen = open * verticalPixelRatio;
            const yHigh = high * verticalPixelRatio;
            const yLow = low * verticalPixelRatio;
            const yClose = close * verticalPixelRatio;

            const isUp = bar.originalData.close >= bar.originalData.open;

            const bodyColor = isUp ? options.upColor : options.downColor;
            const borderColor = isUp ? options.borderUpColor : options.borderDownColor;
            const wickColor = isUp ? options.wickUpColor : options.wickDownColor;

            const maxWidth = barSpacing * horizontalPixelRatio * 0.9;
            const barWidth = maxVolume > 0
                ? (bar.originalData.volume / maxVolume) * maxWidth
                : maxWidth;
            const minWidth = 3 * horizontalPixelRatio;
            const finalWidth = Math.max(minWidth, barWidth);
            const halfWidth = finalWidth / 2;

            const barTop = Math.min(yOpen, yClose);
            const barHeight = Math.abs(yOpen - yClose);
            const finalHeight = Math.max(verticalPixelRatio, barHeight);

            if (options.wickVisible !== false) {
                ctx.beginPath();
                ctx.strokeStyle = wickColor;
                ctx.lineWidth = wickWidth;
                if (isUp) {
                    ctx.moveTo(x, yHigh);
                    ctx.lineTo(x, yClose);
                    ctx.moveTo(x, yOpen);
                    ctx.lineTo(x, yLow);
                } else {
                    ctx.moveTo(x, yHigh);
                    ctx.lineTo(x, yOpen);
                    ctx.moveTo(x, yClose);
                    ctx.lineTo(x, yLow);
                }
                ctx.stroke();
            }

            if (options.visible !== false) {
                ctx.fillStyle = bodyColor;
                ctx.fillRect(x - halfWidth, barTop, finalWidth, finalHeight);
            }

            if (options.borderVisible) {
                ctx.strokeStyle = borderColor;
                ctx.lineWidth = horizontalPixelRatio;
                ctx.strokeRect(x - halfWidth, barTop, finalWidth, finalHeight);
            }
        }
    }
}
